import { useMemo } from 'react';

import type { V1CredentialInfo } from '@/generated/hooks/grimoire/models';
import { useRetrieveAllWorkspaceBookConnections } from '@/generated/hooks/bdk/v2/services/library';
import type { V2BookConnectionDescriptor } from '@/generated/hooks/bdk/models';
import { isDisplayableAutomationConnection } from '@/modules/integrations/utils';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import { buildConnectionsFilter } from './rail-utils';

/** What the rail's Integrations block renders. */
export interface IRailIntegrations {
  connections: V1CredentialInfo[];
  connectionDescriptorById: ReadonlyMap<string, V2BookConnectionDescriptor>;
}

/**
 * The Integrations block's data, read by the always-mounted rail.
 *
 * @param credentialsByAlias - The manifest's alias→credential lookup
 * @returns The Integrations block's data
 */
export function useRailIntegrations(
  credentialsByAlias: ReadonlyMap<string, V1CredentialInfo>,
): IRailIntegrations {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  // `credentialsByAlias` is the unfiltered alias→credential lookup that
  // `resolveStepConnections` needs, so the display filter is applied here
  // rather than to the map. Same predicate the integrations panel groups by:
  // listing a card the panel then reports as "no integrations" is worse than
  // omitting it.
  const connections = useMemo(
    () =>
      [...credentialsByAlias.values()].filter(
        isDisplayableAutomationConnection,
      ),
    [credentialsByAlias],
  );

  // Connection state is not on V1CredentialInfo, so it comes from the
  // workspace's own connections — the same source the drafts integrations
  // panel reads. Fetched once here and looked up per card by connection id.
  const connectionsFilter = useMemo(
    () => buildConnectionsFilter(connections.map(c => c.connection_id)),
    [connections],
  );

  const { data: workspaceConnectionsData } =
    useRetrieveAllWorkspaceBookConnections(
      organizationId,
      workspaceId,
      connectionsFilter ? { filter: connectionsFilter } : undefined,
      {
        query: {
          enabled: Boolean(organizationId && workspaceId && connectionsFilter),
        },
      },
    );

  const connectionDescriptorById = useMemo(() => {
    const byId = new Map<string, V2BookConnectionDescriptor>();
    for (const descriptor of workspaceConnectionsData?.data.book_connections ??
      []) {
      if (descriptor.connection_id)
        byId.set(descriptor.connection_id, descriptor);
    }
    return byId;
  }, [workspaceConnectionsData]);

  return { connections, connectionDescriptorById };
}
